import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PExpandAreaTriggerComponent } from '@panaya/ui/p-expand-area-trigger';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { animate, style, transition, trigger } from '@angular/animations';

/**
 * Field component that can display either HTML or Markdown content.
 *
 * ## Historical Context & Design Decision
 *
 * This component originally displayed HTML content using [innerHtml]. In August 2024,
 * markdown support was added for AI/LLM-generated summaries (Feature 26669), which
 * replaced the HTML rendering with ngx-markdown. This broke existing HTML usages.
 *
 * An attempted fix (Defect 29204) created a double-parsing bug where content was:
 * 1. Pre-parsed with marked.parse()
 * 2. Then parsed again by ngx-markdown component
 *
 * ## Solution
 *
 * Added a `mode` input to support both use cases:
 * - `mode="html"`: Direct HTML rendering (default, backwards compatible)
 *   - Use for: entity.action, activityDetails.text with placeholders/CSS classes
 * - `mode="markdown"`: Markdown rendering via ngx-markdown
 *   - Use for: AI-generated summaries, LLM content
 *
 * ## Usage Guidelines
 *
 * **Use HTML mode when:**
 * - Content contains HTML tags, CSS classes, or click handlers
 * - Content is processed by CorrectionActionPlaceholderService
 * - Content has inline styles or attributes
 *
 * **Use Markdown mode when:**
 * - Content is plain text with markdown syntax (headers, lists, bold, etc.)
 * - Content comes from AI/LLM services
 * - Content needs markdown features like code blocks or links
 *
 * @example
 * <!-- HTML mode (default) -->
 * <p-field-html [value]="entity.action" mode="html"></p-field-html>
 *
 * @example
 * <!-- Markdown mode -->
 * <p-field-html [value]="aiSummary" mode="markdown"></p-field-html>
 */
@Component({
    selector: 'p-field-html',
    animations: [
        trigger('reload', [
            transition('* => animated', [
                animate('10ms', style({ opacity: 0.1, filter: 'blur(8px)' })),
                animate('110ms', style({ opacity: 0.3, filter: 'blur(4px)' })),
                animate('210ms', style({ opacity: 0.5, filter: 'blur(2px)' })),
                animate('310ms', style({ opacity: 0.7, filter: 'blur(1px)' })),
                animate('410ms', style({ opacity: 0.8, filter: 'blur(0.5px)' })),
                animate('510ms', style({ opacity: 0.9, filter: 'blur(0.2px)' })),
                animate('610ms', style({ opacity: 1, filter: 'blur(0px)' }))
            ])
        ])
    ],
    template: `
        <div #htmlField class="p-field-html" [ngClass]="getExpendClass()" [@reload]="state">
            <div *ngIf="mode === 'html'" [innerHtml]="htmlText()"></div>
            <markdown *ngIf="mode === 'markdown'" [data]="value" class="markdown"></markdown>
        </div>
        <p-expand-area-trigger #expandAreaTrigger *ngIf="htmlField" [element]="htmlField" (expandElement)="expandElement($event)">
            <ng-content right-actions select="[actions]"></ng-content>
        </p-expand-area-trigger>
    `,
    styleUrls: ['./p-field-html.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PFieldHtmlComponent extends PFieldAbstractComponent<string> implements AfterViewInit, OnChanges {
    @Input() override value = '';
    @Input() onClick?: (value: any) => boolean;
    @Input() mode: 'html' | 'markdown' = 'html';
    @ViewChild('htmlField')
    private readonly htmlField?: ElementRef<HTMLDivElement>;
    @ViewChild('expandAreaTrigger')
    private readonly expandAreaTrigger?: PExpandAreaTriggerComponent;
    @Input() state: string = '';
    private expandClass = '';

    constructor(private domSanitizer: DomSanitizer, cd: ChangeDetectorRef) {
        super(cd);
    }

    public getExpendClass(): string {
        return this.expandClass;
    }

    public ngAfterViewInit(): void {
        if (this.onClick) {
            this.htmlField?.nativeElement.addEventListener('click', this.onLinkClick.bind(this));
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']?.currentValue) {
            // Handle escaped newlines for both modes
            this.value = this.value?.split('\\n').join('\n');
            // Note: HTML mode renders via htmlText(), Markdown mode is parsed by ngx-markdown
            this.expandAreaTrigger?.triggerCheckIsAreaExpandable();
        }
    }

    protected htmlText(): SafeHtml {
        const description = this.value?.split('\n').join('<br/>');
        return this.domSanitizer.bypassSecurityTrustHtml(description);
    }

    protected expandElement(expand: boolean): void {
        if (expand) {
            this.expandClass = 'expended';
        } else {
            this.expandClass = '';
        }
    }

    protected onLinkClick(event: Event): void {
        let stopPropagation;
        if (this.onClick) {
            stopPropagation = this.onClick(event);
        }
        if (stopPropagation) {
            event.stopImmediatePropagation();
            event.preventDefault();
        }
    }
}
